import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ArrowFowardIcon from '@material-ui/icons/ArrowForwardIosRounded';
import { Collapse } from 'react-collapse';
import { useSelector } from 'react-redux';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { selectLocale } from 'models/localization';
import { List } from 'immutable';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import FilterCheckboxOption from './FilterCheckboxOption';

const ArrowContainer = styled.div<{ selected: boolean }>`
  transform: ${({ selected }) => (selected ? 'rotate(-90deg) translateX(-2px)' : 'rotate(90deg) translateX(2px)')};
  display: inline-block;
  transition: inherit;
  padding: 4px;
`;

const StyledArrowFowardIcon = styled(ArrowFowardIcon)`
  font-size: 12px;
`;

const IndentedCheckbox = styled.div`
  margin-left: 20px;
`;

const FilterListContainer = styled.div`
  width: 100%;
  max-height: 350px;
  overflow: auto;
`;

const MobileFilterSelect = styled.div`
  font-size: 15px;
  line-height: 18px;
`;

const FilterTitleContainer = styled.div<{ singleLevel?: boolean; selected?: boolean; color: string }>`
  cursor: pointer;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: ${({ singleLevel }) => (singleLevel ? 'flex-start' : 'space-between')};
  transition: all 0.3s;
  color:
    ${({ selected, singleLevel, color }) => {
    const selectedColor = color;
    const notSelectedColor = singleLevel ? '#484848' : '#A2AAB3';
    return selected ? selectedColor : notSelectedColor;
  }};
`;

const FilterTitleText = styled.span`
  font-weight: bold;
  font-size: 15px;
  padding: 9px 0;
  display: inline-block;
  text-transform: none;
`;

const QuestionLabel = styled.span`
  font-weight: bold;
  margin-left: 8px;
`;

const RootContainer = ({ isRootQuetion, isOpened, children }: any) => (
  isRootQuetion ? <Collapse isOpened={isOpened}>{children}</Collapse> : <div>{children}</div>
);

const FilterOption = ({
  question,
  choice,
  onChoiceSelect,
  handleQuestionOpen,
  isLoading,
  mobileParticipantFilter,
  isDesktop,
}: any) => {
  const participantFilter = useSelector((state: any) => state.participantFilter);
  const mobileFilters = mobileParticipantFilter?.get('filters');
  const filters = useMemo(() => participantFilter?.get('filters'), [participantFilter]);
  const locale = useSelector(selectLocale);
  const [isChecked, setIsChecked] = useState<boolean>((filters.get(question.id) || List()).includes(choice.id));

  const OnOptionClick = () => {
    if (isLoading) return;
    if (isDesktop) {
      setIsChecked(!isChecked);
    }
    onChoiceSelect(question.id, choice.id, choice.subQuestions.length > 0, question.queryField);
  };

  useEffect(() => {
    if (isDesktop) {
      if (filters.size === 0) {
        setIsChecked((filters.get(question.id) || List()).includes(choice.id));
      }
    } else {
      setIsChecked((mobileFilters?.get(question.id) || List()).includes(choice.id));
    }
  }, [filters, choice.id, mobileFilters, question.id]);

  return (
    <>
      <FilterCheckboxOption
        label={findAvailableLocale(choice.choiceText, locale)}
        questionId={question.id}
        choiceId={choice.id}
        key={`${question.id} - ${choice.id}`}
        checked={isChecked}
        onClick={OnOptionClick}
        hasSubQuestions={choice.subQuestions.length > 0}
      />
      {
        isChecked && choice.subQuestions && Array.isArray(choice.subQuestions) && (
          <IndentedCheckbox>
            {
              choice.subQuestions && Array.isArray(choice.subQuestions) && (
                <ParticipantChoicesFilter
                  questions={choice.subQuestions}
                  onChoiceSelect={onChoiceSelect}
                  isRootQuestion={false}
                  handleQuestionOpen={handleQuestionOpen}
                  isLoading={isLoading}
                  mobileParticipantFilter={mobileParticipantFilter}
                  isDesktop={isDesktop}
                />
              )
            }
          </IndentedCheckbox>
        )
      }
    </>
  );
};

const ParticipantChoicesFilter = ({
  questions,
  onChoiceSelect,
  isRootQuestion = false,
  handleQuestionOpen,
  isLoading,
  mobileParticipantFilter,
  isDesktop,
}: {
  questions: any;
  onChoiceSelect: (questionId: string, choiceId: string, hasSubQuestions: boolean) => void;
  isRootQuestion: boolean;
  handleQuestionOpen: (questionId: string) => void;
  isLoading: boolean;
  mobileParticipantFilter?: any;
  isDesktop: boolean;
}) => {
  const locale = useSelector(selectLocale);
  const participantFilter = useSelector((state: any) => state.participantFilter);
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const openedQuestions = useMemo(() => participantFilter?.get('openedQuestionIds'), [participantFilter]);

  const collapseOnClick = (questionId: string) => {
    handleQuestionOpen(questionId);
  };

  const renderFilterOptions = (question: any) => (
    <>
      {
        isRootQuestion && (
          <FilterTitleText onClick={() => collapseOnClick(question.id)}>
            <ArrowContainer selected={openedQuestions.includes(question.id)}>
              <StyledArrowFowardIcon />
            </ArrowContainer>
            <QuestionLabel>{findAvailableLocale(question.questionText, locale)}</QuestionLabel>
          </FilterTitleText>
        )
      }
      <RootContainer isRootQuetion={isRootQuestion} isOpened={openedQuestions.includes(question.id)}>
        {
          question.choices.map((choice) => (
            <FilterOption
              question={question}
              choice={choice}
              onChoiceSelect={onChoiceSelect}
              handleQuestionOpen={handleQuestionOpen}
              isLoading={isLoading}
              mobileParticipantFilter={isDesktop ? null : mobileParticipantFilter}
              isDesktop={isDesktop}
            />
          ))
        }
      </RootContainer>
    </>
  );

  return (
    <>
      {
        isDesktop ? (
          questions.map((question) => (
            renderFilterOptions(question)
          ))
        ) : (
          <FilterListContainer>
            {
              questions.map((question) => (
                <MobileFilterSelect key={question.id}>
                  <FilterTitleContainer color={themeColor || DEFAULT_THEME_COLOR}>
                    {renderFilterOptions(question)}
                  </FilterTitleContainer>
                </MobileFilterSelect>
              ))
            }
          </FilterListContainer>
        )
      }
    </>
  );
};

export {
  ArrowContainer,
  ParticipantChoicesFilter,
};
